/**
 * useLoginActions — die vier Submit-Handler des Login-Wizards plus die
 * Org-Setup-Vervollstaendigung (UX-Audit Phase 5).
 *
 * Vorher lagen alle vier Handler + `finalizeOrgSetup` direkt in
 * `pages/Login.tsx` (936 LOC God-Component). Der Hook bringt die
 * I/O-Schicht in eine separate Datei und reduziert die Page auf
 * State-Management + Routing zwischen Sub-Components.
 *
 * Verhalten ist **byte-identisch** zur urspruenglichen Implementierung —
 * dieselben supabase-Calls, dieselbe Reihenfolge (`auth.signUp` ->
 * `finalizeOrgSetup` -> profile.update), dasselbe Pending-Org-Pattern
 * fuer den E-Mail-Confirmation-Flow.
 *
 * Konvention: jede Action ist eine `async (values) => void`-Funktion,
 * die Fehler ueber `toast.error` rapportiert (nicht throw't). So bleibt
 * RHF's `formState.isSubmitting` mit der Submit-Promise korrekt.
 */

import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { supabase, supabasePortal } from '@/integrations/supabase/client';
import { FEATURE_PLANS, type SettingType } from '@/hooks/useFeatures';
import type {
  FachpersonSigninInput,
  FachpersonSignupInput,
  PatientSigninInput,
  PatientSignupInput,
} from '@/schemas/auth';

// LocalStorage-Key fuer noch-nicht-finalisierte Org-Setups (E-Mail-
// Confirmation-Flow). Wird beim ersten erfolgreichen Login eingeloest.
export const PENDING_ORG_KEY = 'nutriflow_pending_org';

export type PendingOrg = {
  name: string;
  email: string;
  setting_type: SettingType;
  enabled_features: string[];
  first_name: string;
  last_name: string;
};

interface LoginActionsOptions {
  /**
   * Aktueller Mode aus dem ModePicker (oder null waehrend der
   * Auswahl). Wird in `finalizeOrgSetup` als Default benutzt.
   */
  mode: SettingType | null;
}

/**
 * Voll-Org-Setup nach Auth-Erfolg.
 *
 * Warum wir die `id` clientseitig generieren und `.select().single()`
 * weglassen: die `org_select` RLS-Policy ist `id = get_my_org_id()`,
 * und im Moment des INSERTs ist `profile.organization_id` noch NULL
 * (`get_my_org_id()` returns NULL). Weil PostgREST + Postgres die
 * SELECT-Policy auf RETURNING-Rows enforcen, fuehrt das zu einem
 * falschen "new row violates RLS"-Fehler. Vorgeneriert + plain
 * INSERT ohne RETURNING umgeht das; das nachfolgende profile-UPDATE
 * verknuepft die beiden Rows und macht die Org sichtbar.
 */
async function finalizeOrgSetup(userId: string, pending: PendingOrg) {
  const orgId = crypto.randomUUID();
  const { error: orgError } = await supabase
    .from('organizations')
    .insert({
      id: orgId,
      name: pending.name,
      email: pending.email,
      setting_type: pending.setting_type,
      enabled_features: pending.enabled_features,
    });
  if (orgError) throw orgError;
  const { error: profileError } = await supabase
    .from('profiles')
    .update({
      organization_id: orgId,
      first_name: pending.first_name,
      last_name: pending.last_name,
    })
    .eq('user_id', userId);
  if (profileError) throw profileError;
}

/**
 * Maps raw Supabase auth error messages to user-friendly German strings.
 * Keeps error strings out of individual components so they stay consistent.
 */
export function mapAuthError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('invalid login credentials') || m.includes('invalid credentials')) {
    return 'E-Mail-Adresse oder Passwort ist falsch.';
  }
  if (m.includes('email not confirmed')) {
    return 'Bitte bestätigen Sie Ihre E-Mail-Adresse, bevor Sie sich anmelden.';
  }
  if (m.includes('too many requests') || m.includes('rate limit')) {
    return 'Zu viele Anmeldeversuche. Bitte warten Sie kurz.';
  }
  if (m.includes('user not found') || m.includes('no user found')) {
    return 'Es existiert kein Konto mit dieser E-Mail-Adresse.';
  }
  return message;
}

export function useLoginActions({ mode }: LoginActionsOptions) {
  const navigate = useNavigate();

  const handleForgotPassword = async (email: string) => {
    if (!email) {
      toast.error('Bitte geben Sie Ihre E-Mail-Adresse ein.');
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    });
    if (error) {
      toast.error(mapAuthError(error.message));
    } else {
      toast.success('E-Mail zum Zurücksetzen des Passworts wurde gesendet.');
    }
  };

  const handleFachpersonSignin = async (values: FachpersonSigninInput) => {
    try {
      const { data: loginData, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });
      if (error) throw error;

      // Falls ein Signup mit E-Mail-Confirmation pending ist, jetzt
      // Org-Setup nachholen.
      const pendingJson = localStorage.getItem(PENDING_ORG_KEY);
      if (pendingJson && loginData.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', loginData.user.id)
          .maybeSingle();
        if (profile && !profile.organization_id) {
          try {
            const pending = JSON.parse(pendingJson) as PendingOrg;
            await finalizeOrgSetup(loginData.user.id, pending);
            localStorage.removeItem(PENDING_ORG_KEY);
            // Hard reload, damit AuthContext das frisch verknuepfte
            // Profil neu laedt statt am stale (null org) zu haengen.
            window.location.assign('/dashboard');
            return;
          } catch (finalizeErr) {
            localStorage.removeItem(PENDING_ORG_KEY);
            throw finalizeErr;
          }
        }
        // Profile hat schon eine Org — stale pending entry, einfach droppen.
        localStorage.removeItem(PENDING_ORG_KEY);
      }
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.';
      toast.error(mapAuthError(raw));
    }
  };

  const handleFachpersonSignup = async (values: FachpersonSignupInput) => {
    try {
      const effectiveMode: SettingType = mode ?? 'praxis';
      // The new org lead is the org_admin of their org, praxis or klinik
      // alike (organizations.setting_type carries that distinction, not
      // the role). The historical `'admin'` value was a relic of the
      // pre-separation model where every org lead doubled as a platform
      // admin — that double-hat is the whole problem this PR is fixing.
      const signupRole = 'org_admin';
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { first_name: values.firstName, last_name: values.lastName, role: signupRole },
        },
      });
      if (authError) throw authError;
      const pendingOrg: PendingOrg = {
        name: values.orgName,
        email: values.email,
        setting_type: effectiveMode,
        enabled_features: FEATURE_PLANS[effectiveMode],
        first_name: values.firstName,
        last_name: values.lastName,
      };

      // E-Mail-Confirmation aus? Dann gibt's sofort eine Session und wir
      // legen die Org direkt an. Sonst Pending-Entry stashen und beim
      // ersten Login einloesen.
      if (authData.session && authData.user) {
        await finalizeOrgSetup(authData.user.id, pendingOrg);
        localStorage.removeItem(PENDING_ORG_KEY);
        toast.success('Konto erstellt!');
      } else {
        localStorage.setItem(PENDING_ORG_KEY, JSON.stringify(pendingOrg));
        toast.success('Konto erstellt! Bitte bestätigen Sie Ihre E-Mail, bevor Sie sich anmelden.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.';
      toast.error(message);
    }
  };

  const handlePatientSignin = async (values: PatientSigninInput) => {
    try {
      const { error } = await supabasePortal.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });
      if (error) throw error;
      navigate('/portal', { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.';
      toast.error(message);
    }
  };

  const handlePatientSignup = async (values: PatientSignupInput) => {
    try {
      const { error } = await supabasePortal.auth.signUp({
        email: values.email,
        password: values.password,
        options: { emailRedirectTo: `${window.location.origin}/portal` },
      });
      if (error) throw error;
      toast.success('Konto erstellt! Bitte bestätigen Sie Ihre E-Mail.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten.';
      toast.error(message);
    }
  };

  return {
    handleForgotPassword,
    handleFachpersonSignin,
    handleFachpersonSignup,
    handlePatientSignin,
    handlePatientSignup,
  };
}
